import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { PasswordStrengthIndicator } from './PasswordStrengthIndicator'
import { useAuth } from '../contexts/AuthContext'

export function UpdatePassword() {
  const { clearPasswordSetup } = useAuth()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    // Supabase가 자동으로 세션을 복원하는지 확인
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        // 비밀번호 복구 이벤트 확인됨
        console.log('Password recovery event detected')
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      // 비밀번호 일치 확인
      if (newPassword !== confirmPassword) {
        throw new Error('비밀번호가 일치하지 않습니다')
      }

      // 비밀번호 최소 길이 확인
      if (newPassword.length < 6) {
        throw new Error('비밀번호는 최소 6자 이상이어야 합니다')
      }

      // 비밀번호 업데이트
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      })

      if (updateError) {
        throw updateError
      }

      setSuccess(true)

      // 비밀번호 설정 플래그 해제
      clearPasswordSetup()

      // 2초 후 로그인 페이지로 이동
      setTimeout(() => {
        navigate('/login')
      }, 2000)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '비밀번호 변경에 실패했습니다'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-light font-display">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center">
              <span className="material-symbols-outlined text-white text-4xl">lock_reset</span>
            </div>
          </div>
          <h2 className="text-3xl font-bold tracking-tight text-gray-900">
            새 비밀번호 설정
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            새로운 비밀번호를 입력하세요
          </p>
        </div>

        {success ? (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <span className="material-symbols-outlined text-base">check_circle</span>
              <span className="text-sm">
                비밀번호가 성공적으로 변경되었습니다. 로그인 페이지로 이동합니다.
              </span>
            </div>
          </div>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
                <span className="material-symbols-outlined text-base">error</span>
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                새 비밀번호
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="material-symbols-outlined text-gray-400 text-xl">key</span>
                </div>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="form-input block w-full pl-10 pr-3 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="새 비밀번호를 입력하세요"
                />
              </div>
              <PasswordStrengthIndicator password={newPassword} />
              <p className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">info</span>
                <span>최소 6자 이상, 대소문자와 숫자, 특수문자를 포함하면 더 안전합니다</span>
              </p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                새 비밀번호 확인
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <span className="material-symbols-outlined text-gray-400 text-xl">check_circle</span>
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="form-input block w-full pl-10 pr-3 py-2.5 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary"
                  placeholder="새 비밀번호를 다시 입력하세요"
                />
              </div>
              {newPassword && confirmPassword && (
                <div className="mt-2">
                  {newPassword === confirmPassword ? (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <span className="material-symbols-outlined text-sm">check_circle</span>
                      <span>비밀번호가 일치합니다</span>
                    </p>
                  ) : (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <span className="material-symbols-outlined text-sm">cancel</span>
                      <span>비밀번호가 일치하지 않습니다</span>
                    </p>
                  )}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 border border-transparent rounded-lg text-sm font-bold tracking-wide text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <span className="material-symbols-outlined animate-spin">progress_activity</span>
                  <span>변경 중...</span>
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined">save</span>
                  <span>비밀번호 변경</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}
